import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Input,
  LightModeSemanticColors,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import {
  TABLE_BODY_CELL_STYLE,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import { KOMA_COUNT } from '~/constants/koma'
import { isEnterKey } from '~/libs/keyboard'
import {
  type CellPos,
  isInRange,
  isMultiCellRange,
  makeRange,
  parseTsv,
  rangeToTsv,
  stepCell,
} from '~/models/gridSelection'
import {
  KOMA_DIVIDER_BORDER,
  isKomaIndexDividerAfter,
} from '~/models/komaDivider'
import {
  parseSlotValue,
  planSlotPaste,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import {
  slotEndLabel,
  slotStartLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/slotTime'

const NS =
  'page:adminOcctoMaster.splitSupplyContract.basePattern.modal' as const
const TIME_COL = 0
const VALUE_COL = 1
const BOUNDS = { rowCount: KOMA_COUNT, colCount: 2 }

type Props = {
  /** 48 個 (入力途中の文字列のまま)。 */
  values: readonly string[]
  onChange: (next: string[]) => void
  invalidIndex?: number
}

const rangeLabel = (row: number): string =>
  `${slotStartLabel(row)}〜${slotEndLabel(row)}`

/**
 * 48 コマの値の表 (Excel 風)。 セルのクリック選択・ドラッグ範囲選択・矢印キー移動
 * (Shift で範囲拡張)・F2 / Enter / 文字入力で編集開始・Enter で確定して下へ・Esc で取消・
 * Tab で右へ (行末は次の行の先頭へ折り返す)・Ctrl+C で選んだ範囲だけを TSV コピー
 * (見出しは付けない)・Ctrl+V で選択セルから貼り付け (縦 48 / 横 48 とも。 数値にならない
 * セルは反映せず件数を通知する)・Delete で選択範囲を空にする・Ctrl+Z / Ctrl+Y で元に戻す /
 * やり直す (この表を開いている間の編集だけ)。 見出しは「ただのセル」として
 * 選択・コピーの対象にする。 値の検査は OK 時 (`validatePatternDraft`) が行う。
 */
export const SlotValueGrid = ({
  values,
  onChange,
  invalidIndex,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const [anchor, setAnchor] = useState<CellPos>({ row: 0, col: VALUE_COL })
  const [focus, setFocus] = useState<CellPos>({ row: 0, col: VALUE_COL })
  const [editing, setEditingState] = useState<{
    row: number
    draft: string
  } | null>(null)
  // Enter / Esc で input が外れる時の blur が、 確定済みの編集をもう一度確定しないよう
  // 最新の編集状態は ref からも読む。
  const editingRef = useRef(editing)
  const setEditing = (next: { row: number; draft: string } | null): void => {
    editingRef.current = next
    setEditingState(next)
  }
  const [pasteSkippedCount, setPasteSkippedCount] = useState(0)
  // 履歴は表の中の編集だけを持つ。 モーダルを閉じて表が外れれば消え、 保存前の編集にだけ効く
  const undoStack = useRef<string[][]>([])
  const redoStack = useRef<string[][]>([])
  const range = makeRange(anchor, focus)

  useEffect(() => {
    const stop = (): void => {
      dragging.current = false
    }
    window.addEventListener('mouseup', stop)
    return () => {
      window.removeEventListener('mouseup', stop)
    }
  }, [])

  const select = (pos: CellPos, extend: boolean): void => {
    setFocus(pos)
    if (!extend) setAnchor(pos)
  }

  const setValues = (
    updates: Array<{ index: number; value: string }>,
  ): void => {
    setPasteSkippedCount(0)
    const next = [...values]
    for (const { index, value } of updates) next[index] = value
    // 値が変わらない操作は履歴に積まない (元に戻しても何も起きない手数を作らない)
    if (next.every((v, i) => v === values[i])) return
    undoStack.current.push(values.slice())
    redoStack.current = []
    onChange(next)
  }

  const undo = (): void => {
    const prev = undoStack.current.pop()
    if (prev === undefined) return
    setPasteSkippedCount(0)
    redoStack.current.push(values.slice())
    onChange(prev)
  }

  const redo = (): void => {
    const next = redoStack.current.pop()
    if (next === undefined) return
    setPasteSkippedCount(0)
    undoStack.current.push(values.slice())
    onChange(next)
  }

  // Tab の移動先 (右へ、 行末は次の行の先頭へ。 Shift は逆向き)。 表の端では null
  const tabTarget = (pos: CellPos, backward: boolean): CellPos | null => {
    const dir = backward ? -1 : 1
    const sameRow = stepCell(pos, 0, dir, BOUNDS)
    if (sameRow !== null) return sameRow
    return stepCell(
      { row: pos.row, col: backward ? BOUNDS.colCount : -1 },
      dir,
      dir,
      BOUNDS,
    )
  }

  const startEdit = (row: number, initial?: string): void => {
    setEditing({ row, draft: initial ?? values[row] ?? '' })
  }

  const finishEdit = (
    commit: boolean,
    move: 'down' | 'tab' | 'backTab' | null,
  ): void => {
    const current = editingRef.current
    if (current === null) return
    if (commit) setValues([{ index: current.row, value: current.draft }])
    setEditing(null)
    if (move !== null) {
      const here = { row: current.row, col: VALUE_COL }
      const next =
        move === 'down'
          ? stepCell(here, 1, 0, BOUNDS)
          : tabTarget(here, move === 'backTab')
      select(next ?? here, false)
    }
    rootRef.current?.focus()
  }

  // 編集中の Esc は編集の取消 (conventions の 48 コマのグリッド)。モーダル (Zag Dialog) は Esc を
  // document の capture で受けるので、セルの onKeyDown では先に閉じてしまう。window の capture で
  // 先に受けて止める (useDropdownDismiss と同じ方式)。IME の変換中の Esc は変換の取消なので取消にしない
  const hasEdit = editing !== null
  const finishEditRef = useRef(finishEdit)
  finishEditRef.current = finishEdit
  useEffect(() => {
    if (!hasEdit) return
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (e.isComposing || e.keyCode === 229) return
      e.preventDefault()
      finishEditRef.current(false, null)
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [hasEdit])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    if (e.ctrlKey || e.metaKey) {
      const key = e.key.toLowerCase()
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault()
        redo()
      }
      return
    }
    if (e.key === 'Tab') {
      // 表の端では移動先が無いので、 キーボードの罠にならないようブラウザの Tab に任せる
      const next = tabTarget(focus, e.shiftKey)
      if (next === null) return
      e.preventDefault()
      select(next, false)
      return
    }
    const arrows: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }
    const delta = arrows[e.key]
    if (delta !== undefined) {
      e.preventDefault()
      const next = stepCell(focus, delta[0], delta[1], BOUNDS) ?? focus
      select(next, e.shiftKey)
      return
    }
    if (e.key === 'F2' || isEnterKey(e)) {
      e.preventDefault()
      if (focus.col === VALUE_COL) startEdit(focus.row)
      return
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      if (range.colMax >= VALUE_COL) {
        const updates: Array<{ index: number; value: string }> = []
        for (let row = range.rowMin; row <= range.rowMax; row++) {
          updates.push({ index: row, value: '' })
        }
        setValues(updates)
      }
      return
    }
    if (e.key.length === 1 && !e.altKey && focus.col === VALUE_COL) {
      e.preventDefault()
      startEdit(focus.row, e.key)
    }
  }

  const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    e.preventDefault()
    e.clipboardData.setData(
      'text/plain',
      rangeToTsv(range, (row, col) =>
        col === TIME_COL ? rangeLabel(row) : (values[row] ?? ''),
      ),
    )
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    e.preventDefault()
    const text = e.clipboardData.getData('text')
    const matrix = parseTsv(text)
    const single = matrix.length === 1 && matrix[0]?.length === 1
    if (single && isMultiCellRange(range)) {
      // 1 つの値は選んだ範囲の値セルすべてへ (数値にならない値は反映しない)
      const updates: Array<{ index: number; value: string }> = []
      const value = matrix[0]?.[0] ?? ''
      const rowCount = range.rowMax - range.rowMin + 1
      if (parseSlotValue(value) !== undefined) {
        for (let row = range.rowMin; row <= range.rowMax; row++) {
          updates.push({ index: row, value })
        }
      }
      setValues(updates)
      setPasteSkippedCount(updates.length === 0 ? rowCount : 0)
      return
    }
    const { cells, skippedCount } = planSlotPaste(text, range.rowMin)
    setValues(cells)
    setPasteSkippedCount(skippedCount)
    if (cells.length > 0) {
      const last = cells[cells.length - 1]?.index ?? range.rowMin
      setAnchor({ row: range.rowMin, col: VALUE_COL })
      setFocus({ row: last, col: VALUE_COL })
    }
  }

  return (
    <>
      {pasteSkippedCount > 0 && (
        <Box marginBottom={2}>
          <CompactAlert
            role="alert"
            status="warning"
            title={t(`${NS}.pasteSkipped`, { count: pasteSkippedCount })}
          />
        </Box>
      )}
      <Box
        ref={rootRef}
        overflowY="auto"
        maxHeight="320px"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
        _focusVisible={{ outline: '2px solid', outlineColor: 'Focus.clarity' }}
        aria-label={t(`${NS}.gridAria`)}
        onCopy={handleCopy}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        role="grid"
        tabIndex={0}
      >
        <table
          style={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr role="row">
              <th style={{ ...TABLE_HEADER_CELL_STYLE, width: '50%' }}>
                {t(`${NS}.slotColumn`)}
              </th>
              <th
                style={{
                  ...TABLE_HEADER_CELL_STYLE,
                  width: '50%',
                  textAlign: 'right',
                }}
              >
                {t(`${NS}.valueColumn`)}
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: KOMA_COUNT }, (_, row) => (
              <tr key={row} role="row">
                {[TIME_COL, VALUE_COL].map((col) => {
                  const selected = isInRange(range, row, col)
                  const isFocus = focus.row === row && focus.col === col
                  const isEditing = editing?.row === row && col === VALUE_COL
                  return (
                    <td
                      key={col}
                      aria-invalid={
                        col === VALUE_COL && row === invalidIndex
                          ? true
                          : undefined
                      }
                      aria-label={
                        col === VALUE_COL
                          ? t(`${NS}.slotValueAria`, { range: rangeLabel(row) })
                          : undefined
                      }
                      aria-selected={selected}
                      data-focused={isFocus || undefined}
                      data-koma-divider-after={
                        isKomaIndexDividerAfter(row) || undefined
                      }
                      onDoubleClick={() => {
                        if (col === VALUE_COL) startEdit(row)
                      }}
                      onMouseDown={(e) => {
                        if (isEditing) return
                        if (editing !== null) finishEdit(true, null)
                        dragging.current = true
                        select({ row, col }, e.shiftKey)
                        rootRef.current?.focus()
                      }}
                      onMouseEnter={() => {
                        if (dragging.current) setFocus({ row, col })
                      }}
                      role="gridcell"
                      style={{
                        ...TABLE_BODY_CELL_STYLE,
                        ...(isKomaIndexDividerAfter(row)
                          ? { borderBottom: KOMA_DIVIDER_BORDER }
                          : {}),
                        lineHeight: '20px',
                        ...(col === VALUE_COL
                          ? {
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                            }
                          : {}),
                        cursor: 'cell',
                        userSelect: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        backgroundColor: selected
                          ? LightModeSemanticColors.Surface
                              .accentPrimaryLightMid.value
                          : undefined,
                        outline:
                          col === VALUE_COL && row === invalidIndex
                            ? `2px solid ${LightModeSemanticColors.Border.negativeMid.value}`
                            : isFocus
                              ? `2px solid ${LightModeSemanticColors.Surface.accentPrimary.value}`
                              : undefined,
                        outlineOffset: -2,
                      }}
                    >
                      {isEditing ? (
                        <Input
                          ref={(el) => {
                            el?.focus()
                          }}
                          textAlign="right"
                          aria-label={t(`${NS}.slotValueEditAria`, {
                            range: rangeLabel(row),
                          })}
                          inputMode="numeric"
                          onBlur={() => {
                            finishEdit(true, null)
                          }}
                          onChange={(e) => {
                            setEditing({ row, draft: e.target.value })
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation()
                            // 日本語 IME の変換中の Enter (変換確定) / Esc (変換取消) はセル操作にしない
                            if (
                              e.nativeEvent.isComposing ||
                              e.keyCode === 229
                            ) {
                              return
                            }
                            if (isEnterKey(e)) {
                              e.preventDefault()
                              finishEdit(true, 'down')
                            } else if (e.key === 'Tab') {
                              e.preventDefault()
                              finishEdit(true, e.shiftKey ? 'backTab' : 'tab')
                            } else if (e.key === 'Escape') {
                              e.preventDefault()
                              e.stopPropagation()
                              finishEdit(false, null)
                            }
                          }}
                          size="sm"
                          value={editing?.draft ?? ''}
                        />
                      ) : col === VALUE_COL ? (
                        (values[row] ?? '')
                      ) : (
                        rangeLabel(row)
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </>
  )
}
